import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { toastFailure } from '@/lib/feedback'
import { sinceText } from '../record/format'
import { chooseMention } from './api'
import { RelationSearch, type RelationPick } from './RelationSearch'
import type { Candidate, Mention, MentionChoiceBody, VoiceCapture } from './types'

type Choice =
  | { kind: 'candidate'; candidate: Candidate }
  | { kind: 'search'; pick: RelationPick | null }
  | { kind: 'new' }
  | { kind: 'none' }

function CandidateText({ candidate }: { candidate: Candidate }) {
  const bits = [candidate.role, candidate.organisation_name].filter(Boolean).join(' · ')
  const last = candidate.last_contact_on ? `laatste contact ${sinceText(candidate.last_contact_on)}` : 'nog geen contact'
  return (
    <span className="flex flex-col">
      <span className="text-base font-medium">{candidate.name}</span>
      {bits ? <span className="text-sm text-muted-foreground">{bits}</span> : null}
      <span className="text-sm text-muted-foreground">{last}</span>
    </span>
  )
}

/**
 * The choice under "Wie" for one mention (bottom sheet below 768 px): the candidates with
 * organisation, role and last contact, someone else (search), a new contact, or no link.
 * It posts the choice and hands the updated capture back; nothing else is written.
 */
export function MentionResolver({
  captureId,
  mention,
  open,
  onClose,
  onResolved,
}: {
  captureId: string
  mention: Mention
  open: boolean
  onClose: () => void
  onResolved: (capture: VoiceCapture) => void
}) {
  const [choice, setChoice] = useState<Choice | null>(null)
  const [fullName, setFullName] = useState(mention.name)
  const [organisation, setOrganisation] = useState(mention.organisation_hint ?? '')
  const [email, setEmail] = useState('')
  const [saving, setSaving] = useState(false)

  function body(): MentionChoiceBody | null {
    if (!choice) return null
    if (choice.kind === 'candidate') return { type: choice.candidate.kind, id: choice.candidate.id }
    if (choice.kind === 'search') return choice.pick ? { type: choice.pick.type, id: choice.pick.id } : null
    if (choice.kind === 'none') return { type: 'geen' }
    if (!fullName.trim()) return null
    return {
      type: 'nieuw_contact',
      nieuw: {
        full_name: fullName.trim(),
        ...(organisation.trim() ? { organisation_name: organisation.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
      },
    }
  }

  const ready = body()
  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      onResolved(await chooseMention(captureId, mention.ref, ready))
      onClose()
    } catch (caught) {
      toastFailure(caught, 'Je keuze kon niet worden opgeslagen.')
    } finally {
      setSaving(false)
    }
  }

  const radio = (id: string, selected: boolean, onSelect: () => void, children: React.ReactNode) => (
    <label key={id} className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border border-border p-3">
      <input
        type="radio"
        name={`mention-${mention.ref}`}
        className="mt-1 size-4 accent-primary"
        checked={selected}
        onChange={onSelect}
      />
      {children}
    </label>
  )

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{`Wie bedoel je met '${mention.name}'?`}</DialogTitle>
          <DialogDescription>
            Kies de relatie. Er wordt niets vastgelegd voordat je op Vastleggen tikt.
          </DialogDescription>
        </DialogHeader>
        <div className="flex max-h-[60svh] flex-col gap-2 overflow-y-auto">
          {mention.candidates.map((candidate) =>
            radio(
              `${candidate.kind}:${candidate.id}`,
              choice?.kind === 'candidate' && choice.candidate.id === candidate.id,
              () => setChoice({ kind: 'candidate', candidate }),
              <CandidateText candidate={candidate} />,
            ),
          )}
          {radio(
            'search',
            choice?.kind === 'search',
            () => setChoice({ kind: 'search', pick: null }),
            <span className="text-base">Iemand anders…</span>,
          )}
          {choice?.kind === 'search' ? (
            <div className="space-y-2 pl-7">
              <RelationSearch label="Zoek een relatie" onPick={(pick) => setChoice({ kind: 'search', pick })} />
              {choice.pick ? <p className="text-sm">{`Gekozen: ${choice.pick.name}`}</p> : null}
            </div>
          ) : null}
          {radio(
            'new',
            choice?.kind === 'new',
            () => setChoice({ kind: 'new' }),
            <span className="text-base">{`Nieuw contact '${mention.name}'`}</span>,
          )}
          {choice?.kind === 'new' ? (
            <div className="space-y-3 pl-7">
              <FormField label="Volledige naam" required>
                <Input value={fullName} onChange={(event) => setFullName(event.target.value)} />
              </FormField>
              <FormField label="Organisatie">
                <Input value={organisation} onChange={(event) => setOrganisation(event.target.value)} />
              </FormField>
              <FormField label="E-mail (optioneel)">
                <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
              </FormField>
            </div>
          ) : null}
          {radio('none', choice?.kind === 'none', () => setChoice({ kind: 'none' }), <span className="text-base">Niet koppelen</span>)}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button type="button" disabled={!ready || saving} onClick={() => void save()}>
            {saving ? 'Opslaan…' : 'Deze keuze gebruiken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

